import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getDelivery, takeOverWorkItem,
  startWorkItem, closeWorkItem, setTaskOwner,
  readServerError,
} from '../../../api/deliveryApi';
import * as live from '../../../api/liveClient';
import { useToast } from '../../Common/ToastProvider';
import WorkItemDrawer from './WorkItemDrawer';
// QLOG-S2.1 §A — segmented Delivery ↔ Plan toggle in the header.
import ProjectViewToggle from '../ProjectViewToggle';
// WI-S4.2 — reuse ProjectDetailPage's CSS as the base language; the
// delivery-only styles that remain in DeliveryPage.css shrink to
// timeline SVG cells, funnels, the month calendar, and the Log-hours
// scaffolding that PDP doesn't have.
import '../ProjectDetailPage.css';
import './DeliveryPage.css';
import { fmtD } from '../planUtils';

// DELIVERY-WI (WI-S1) + WI-S4.2 visual rework:
// DeliveryPage renders inside the .pdp container and reuses the PDP
// class language (thead #f0f4e8, tr.sum.l0 fase rows, td.rn/tn/tch/uc,
// .mi state badge, .ctx menus, .ie-input, .task-ghost-row). Delivery-
// only additions live in DeliveryPage.css (Timeline cells, filter
// funnels, the Log-hours navigator/calendar).

const isoDate = (v) => {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('sv-SE');
};

// QLOG-S2 (UDRAW-S2 refactor) — infoContent builders. Each builder
// derives its banner + cards + child rows from the DeliveryView the
// page already holds. The task DTO travels through so TaskDrawerForm
// (UDRAW-S2 §2.1) can host the Status select + Notes editor + Take
// ownership link on top of the payload. `mode` matches TaskDrawerForm's
// dispatch table (phase / summary / task-with-wis / milestone /
// quickLog).
const dashOrNum = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : `${Number(n).toFixed(1)} h`);

function buildPhaseInfoContent(task) {
  const budget = Number(task.budgetHours || 0);
  const done = Number(task.loggedHours || 0);
  const remaining = Math.max(0, budget - done);
  return {
    kind: 'phase',
    mode: 'phase',
    task,
    banner: {
      tone: 'info',
      text: 'Phase rows are descriptive. Hours are logged on the leaf rows below.',
    },
    cards: budget > 0 || done > 0 ? [
      { label: 'Sold', value: `${budget.toFixed(1)} h` },
      { label: 'Logged', value: `${done.toFixed(1)} h` },
      { label: 'Remaining', value: `${remaining.toFixed(1)} h`, tone: remaining > 0 ? 'ok' : 'over' },
    ] : null,
  };
}

function buildSummaryInfoContent(task, allTasks, onPickTask) {
  const level = task.outlineLevel || 0;
  const startIdx = allTasks.findIndex((x) => x.id === task.id);
  const childRows = [];
  for (let j = startIdx + 1; j < allTasks.length; j++) {
    const c = allTasks[j];
    if ((c.outlineLevel || 0) <= level) break;
    childRows.push({
      id: c.id,
      title: c.name,
      // WBS-RETIRE — every child row is a plain Task now.
      subtitle: 'Task',
      trailing: isoDate(c.plannedEndDate),
      testId: `wi-drawer-info-child-task-${c.id}`,
      onClick: onPickTask ? () => onPickTask({ task: c }) : null,
    });
  }
  const done = Number(task.loggedHours || 0);
  return {
    kind: 'summary',
    mode: 'summary',
    task,
    banner: {
      tone: 'info',
      text: 'This row rolls up its children. Log hours on the leaf rows below.',
    },
    childRows,
    childRowsTitle: 'Child tasks',
    cards: done > 0 ? [
      { label: 'Logged', value: `${done.toFixed(1)} h` },
    ] : null,
  };
}

function buildTaskWithWisInfoContent(task, taskWis, onPickWi) {
  const totalRollup = taskWis.reduce((a, w) => a + (Number(w.rollupHours) || 0), 0);
  const budget = Number(task.budgetHours || 0);
  const remaining = Math.max(0, budget - totalRollup);
  const childRows = taskWis.map((wi) => ({
    id: wi.id,
    title: `WI-${wi.id} · ${wi.title}`,
    subtitle: wi.assigneeFullName ? `Assignee: ${wi.assigneeFullName}` : '',
    trailing: dashOrNum(wi.rollupHours),
    testId: `wi-drawer-info-child-wi-${wi.id}`,
    onClick: () => onPickWi({ wi }),
  }));
  return {
    kind: 'task-with-wis',
    mode: 'task-with-wis',
    task,
    banner: {
      tone: 'warn',
      text: 'This task has work items — pick one below to log hours.',
    },
    childRows,
    childRowsTitle: 'Work items',
    cards: [
      { label: 'Sold', value: `${budget.toFixed(1)} h` },
      { label: 'Logged', value: `${totalRollup.toFixed(1)} h` },
      { label: 'Remaining', value: `${remaining.toFixed(1)} h`, tone: remaining > 0 ? 'ok' : 'over' },
    ],
  };
}

function buildWiInfoContent(wi, kids, onPickChild) {
  const totalRollup = kids.reduce((a, w) => a + (Number(w.rollupHours) || 0), 0);
  return {
    kind: 'wi-with-children',
    badge: `WI-${wi.id}`,
    title: wi.title,
    subtitle: 'Work item with sub-items',
    banner: {
      tone: 'info',
      text: 'This work item has sub-items — pick one below to log hours.',
    },
    cards: [
      { label: 'Σ sub-items', value: dashOrNum(totalRollup) },
    ],
    children: kids.map((c) => ({
      id: c.id,
      title: `WI-${c.id} · ${c.title}`,
      subtitle: c.assigneeFullName ? `Assignee: ${c.assigneeFullName}` : '',
      trailing: dashOrNum(c.rollupHours),
      testId: `wi-drawer-info-child-wi-${c.id}`,
      onClick: () => onPickChild({ wi: c }),
    })),
    childrenTitle: 'Sub-items',
  };
}

// WI-S4.2 — PDP's fmtD gives "Sun 07/06/26"; the whole app formats
// planned dates that way. Reuse it for consistency.
const gridDate = (v) => {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? '' : fmtD(d);
};

// WI-S4.2 — PDP's state badge (.mi.st-{n|i|c}) with the ADO
// forward-only menu. New → N (grey), Active → A (blue), Closed → C
// (green). tooltip carries the full status.
const miClassFor = (status) => status === 'Closed' ? 'st-c' : status === 'Active' ? 'st-i' : 'st-n';
const miLetterFor = (status) => status === 'Closed' ? 'C' : status === 'Active' ? 'A' : 'N';

// UDRAW-S1 §3 (extended UDRAW-S2) — build the flat sub-tree for the
// drawer's sticky-bottom "HIERARCHY" panel: from the anchoring row's
// phase down through its tasks + WIs + sub-WIs, marked with
// `isCurrent` on whichever row the drawer opened on. The anchor can
// be a work item OR a task/phase — task drawers (info + quickLog) now
// use the same tree via UDRAW-S2.
//
// Signature: buildDrawerTreeData(anchor, view, workItemsByTask)
//   anchor: { wi: DeliveryWorkItemDto } — WI drawer path
//        or { task: DeliveryTaskDto }   — task/phase drawer path
// Everything comes off the DeliveryView the page already holds.
function buildDrawerTreeData(anchor, view, workItemsByTask) {
  if (!anchor || !view) return null;
  const tasksList = view.tasks || [];

  let currentWiId = null;
  let currentTaskId = null;
  let anchorTask = null;
  if (anchor.wi) {
    currentWiId = anchor.wi.id;
    anchorTask = tasksList.find((t) => t.id === anchor.wi.projectTaskId) || null;
  } else if (anchor.task) {
    currentTaskId = anchor.task.id;
    anchorTask = anchor.task;
  }
  if (!anchorTask) return null;

  // Walk up to the phase — the shallowest ancestor of the anchoring
  // task. If the anchor is already outlineLevel 0 it IS the phase.
  const taskIdx = tasksList.findIndex((t) => t.id === anchorTask.id);
  let phaseIdx = taskIdx;
  while (phaseIdx > 0 && (tasksList[phaseIdx].outlineLevel || 0) > 0) phaseIdx--;
  const phase = tasksList[phaseIdx];
  const phaseLevel = phase.outlineLevel || 0;

  // Collect the phase's task sub-tree — everything with a deeper
  // outlineLevel until the next same-or-shallower row.
  const subtreeTasks = [phase];
  for (let j = phaseIdx + 1; j < tasksList.length; j++) {
    const t = tasksList[j];
    if ((t.outlineLevel || 0) <= phaseLevel) break;
    subtreeTasks.push(t);
  }

  const treeData = [];
  for (const t of subtreeTasks) {
    const indent = (t.outlineLevel || 0) - phaseLevel;
    const isPhase = t.id === phase.id;
    treeData.push({
      key: `t-${t.id}`,
      kind: isPhase ? 'phase' : 'task',
      badge: null,
      label: t.name,
      hours: null,
      indent,
      isCurrent: currentTaskId === t.id,
      task: t,
    });
    const wisUnder = workItemsByTask.get(t.id) || [];
    const wisByParent = new Map();
    for (const w of wisUnder) {
      const p = w.parentWorkItemId ?? 0;
      if (!wisByParent.has(p)) wisByParent.set(p, []);
      wisByParent.get(p).push(w);
    }
    const walkWi = (list, depth) => {
      for (const w of list) {
        const badge = w.status === 'Closed' ? 'C' : w.status === 'Active' ? 'A' : 'N';
        treeData.push({
          key: `wi-${w.id}`,
          kind: 'wi',
          badge,
          label: `WI-${w.id} · ${w.title}`,
          hours: w.rollupHours != null ? w.rollupHours : (w.ownHours != null ? w.ownHours : null),
          indent: indent + 1 + depth,
          isCurrent: currentWiId === w.id,
          wi: w,
        });
        walkWi(wisByParent.get(w.id) || [], depth + 1);
      }
    };
    walkWi(wisByParent.get(0) || [], 0);
  }
  return treeData;
}

// WI-S3.1 (MAJOR-4) — server error surfacing lives in ../../../api/
// deliveryApi.js as `readServerError`, imported above.

// Nest work items into a per-task adjacency map, honoring SortOrder.
const buildTree = (workItems) => {
  const byParent = new Map(); // parentId (null for roots) → items
  for (const w of workItems) {
    const key = w.parentWorkItemId ?? 0;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(w);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);
  return byParent;
};

// WI-S4.4 — Excel-like value filter for the Work Item column. Shows
// a search line, the "contains…" text filter, and a scrollable
// checklist of the column's DISTINCT values (task names + WI titles
// present in the grid). Empty checklist ⇒ all rows pass; ticking
// narrows to the selected set; unticking the last returns all.
// PO ABSOLUTE RULE: this is the canonical column-filter shape for
// every table in the app.
const TitleFilterPanel = ({ values, titleText, titleValues, onTitleTextChange, onValuesChange }) => {
  const [q, setQ] = React.useState('');
  const filtered = React.useMemo(() => {
    if (!q) return values;
    const lq = q.toLowerCase();
    return values.filter((v) => v.toLowerCase().includes(lq));
  }, [values, q]);
  const toggle = (v) => {
    const n = new Set(titleValues);
    if (n.has(v)) n.delete(v); else n.add(v);
    onValuesChange(n);
  };
  return (
    <>
      {/* WI-S4.6 — Excel hierarchy: (1) list-search line, (2) value
          checklist, (3) separated "Text contains" section below. Same
          shape as State/Assignee panels so the whole grid speaks one
          filter language. */}
      <input type="text"
             data-testid="dlv-filter-title-search"
             placeholder="search values…"
             value={q}
             onChange={(e) => setQ(e.target.value)}
             style={{ width: '100%', padding: 4, boxSizing: 'border-box' }} />
      <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6,
                    border: '1px solid #e0e0e0', borderRadius: 3, padding: 4 }}>
        {filtered.length === 0 && (
          <div style={{ color: '#999', fontSize: 11, padding: 4 }}>No values match.</div>
        )}
        {filtered.map((v) => (
          <label key={v}
                 style={{ display: 'block', fontSize: 12, padding: '2px 0',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                 title={v}>
            <input type="checkbox"
                   data-testid={`dlv-filter-title-val-${v}`}
                   checked={titleValues.has(v)}
                   onChange={() => toggle(v)} /> {v}
          </label>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #e0e0e0', marginTop: 8, paddingTop: 6 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                      color: '#666', fontWeight: 700, marginBottom: 4 }}>
          Text contains
        </div>
        <input type="text"
               data-testid="dlv-filter-title-text"
               placeholder="contains…"
               value={titleText}
               onChange={(e) => onTitleTextChange(e.target.value)}
               style={{ width: '100%', padding: 4, boxSizing: 'border-box' }} />
      </div>
      <button data-testid="dlv-filter-clear-title"
              onClick={() => { onTitleTextChange(''); onValuesChange(new Set()); }}
              style={{ marginTop: 8, fontSize: 11 }}>Clear</button>
    </>
  );
};

// WI-S3 — inline assignee filter panel with a search line (spec §1
// "list search"). Empty selection = show all; toggling checkboxes
// narrows the set. Also carries a virtual "— unassigned —" entry.
const AssigneeFilterPanel = ({ team, selected, onChange }) => {
  const [q, setQ] = React.useState('');
  const options = React.useMemo(() => {
    const list = [{ key: 'null', label: '— unassigned —' }].concat(
      team.map((m) => ({ key: String(m.employeeId), label: m.isFormer ? `${m.fullName} (left)` : m.fullName }))   // CEV-S3
    );
    if (!q) return list;
    const lq = q.toLowerCase();
    return list.filter((o) => o.label.toLowerCase().includes(lq));
  }, [team, q]);
  const toggle = (key) => {
    const n = new Set(selected);
    if (n.has(key)) n.delete(key); else n.add(key);
    onChange(n);
  };
  return (
    <>
      <input type="text"
             data-testid="dlv-filter-assignee-search"
             placeholder="search…"
             value={q}
             onChange={(e) => setQ(e.target.value)}
             style={{ width: '100%', padding: 4, boxSizing: 'border-box' }} />
      <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 6 }}>
        {options.map((o) => (
          <label key={o.key} style={{ display: 'block', fontSize: 12, padding: '2px 0' }}>
            <input type="checkbox"
                   data-testid={`dlv-filter-assignee-${o.key}`}
                   checked={selected.has(o.key)}
                   onChange={() => toggle(o.key)} /> {o.label}
          </label>
        ))}
      </div>
      <button data-testid="dlv-filter-clear-assignee"
              onClick={() => onChange(new Set())}
              style={{ marginTop: 6, fontSize: 11 }}>Clear</button>
    </>
  );
};

const DeliveryPage = () => {
  const { id } = useParams();
  const projectId = Number(id);
  const nav = useNavigate();
  const toast = useToast();

  const [view, setView] = useState(null);   // whole DTO
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // UDRAW-S5-A10 — a live `deliveryChanged` event triggers reload().
  // Pre-A10, reload flipped `loading=true`, causing the render tree
  // below to be replaced with the full-screen "Loading…" branch —
  // which unmounted the drawer, its Discussion, and any typed draft.
  // A10 splits state: `loading` is the COLD-START flag (only true
  // when there's no `view` yet); `refreshing` covers every subsequent
  // reload and never swaps the tree. Errors during silent refresh
  // keep the old `view` and surface as a soft indicator instead of a
  // full-screen error swap.
  const [refreshing, setRefreshing] = useState(false);
  // viewRef mirrors `view` so reload() can decide cold-vs-silent
  // without adding `view` to its useCallback deps (which would cause
  // an infinite reload loop through the "run reload on mount" effect).
  const viewRef = useRef(null);
  useEffect(() => { viewRef.current = view; }, [view]);
  const [collapsed, setCollapsed] = useState(() => new Set());  // work item ids collapsed
  const [collapsedTasks, setCollapsedTasks] = useState(() => new Set());
  // UDRAW-S5-A7 — drawer selection is stored by ID only; the WI/task
  // object is derived from the current `view` on every render (useMemo
  // below). A `deliveryChanged` event triggers `reload()` → `setView` →
  // the derived value automatically refreshes, so the OPEN drawer's
  // read-only sections (title / State / Owner / Role / totals / Notes /
  // Discussion list / HIERARCHY / Work items list) follow the live
  // board without any explicit copy-back step. Local drafts survive
  // because they live in child-component state (dirty-flag guarded in
  // WorkItemDrawer, uncontrolled contenteditable in Discussion), which
  // this refactor does NOT touch. If the item is deleted by another
  // participant, the derived value goes null and the drawer renders a
  // "no longer available" notice instead of the form (no crash, no
  // auto-close — the viewer decides when to close).
  const [drawerWiId, setDrawerWiId] = useState(null);
  // QLOG-S2 — one non-leaf-WI drawer at a time. Shapes:
  //   { kind: 'info',     taskId, infoKind: 'phase' | 'summary' | 'task-with-wis' }
  //   { kind: 'info',     wiId,   infoKind: 'wi-with-children' }
  //   { kind: 'quickLog', taskId }
  // The leaf-WI path stays on `drawerWiId` (unchanged regression).
  const [rowDrawer, setRowDrawer] = useState(null);
  // UDRAW-S3 §2.1 — the ghost row is GONE. WI creation now lives in
  // the drawer (TaskDrawerForm's "+ New work item" and
  // WorkItemDrawer's "+ New child work item" toolbelt buttons). The
  // "+ work item"/"+ child" grid buttons and the context menu entries
  // still exist, but they now OPEN the appropriate drawer with the
  // create form auto-expanded via `autoOpenCreate`. State below is
  // the intent glue: which drawer should auto-open the form next.
  const [autoCreateRequest, setAutoCreateRequest] = useState(null);
  // Shape: { kind: 'task', task } or { kind: 'wi', wi }. Consumed
  // exactly once by the drawer's mount effect, then cleared.
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, target: null });
  // WI-S3 — column show/hide, persisted per project. The default set
  // matches the S1/S2 grid; a user's toggles land in localStorage
  // under the same key shape as hrapp.projects.list.columns.
  const COLS_KEY = `hrapp.delivery.columns.${projectId}`;
  const DEFAULT_COL_VIS = {
    state: true, assignee: true, start: true, finish: true,
    own: true, sigma: true, budget: true, timeline: true,
  };
  const [colVis, setColVis] = useState(() => {
    try {
      const raw = localStorage.getItem(COLS_KEY);
      if (raw) return { ...DEFAULT_COL_VIS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_COL_VIS;
  });
  useEffect(() => {
    try { localStorage.setItem(COLS_KEY, JSON.stringify(colVis)); }
    catch { /* ignore */ }
  }, [COLS_KEY, colVis]);
  // WI-S3 — header filter state. Empty set = no filter (all rows).
  // Populated from a persona default on first load, then user-driven.
  const [filters, setFilters] = useState({
    states: new Set(),        // e.g. {'New','Active'}. Empty = show all.
    lateOnly: false,
    assignees: new Set(),     // employeeIds; string 'null' for "unassigned"
    titleText: '',            // WI-S4.4 — Excel "contains" text filter.
    titleValues: new Set(),   // WI-S4.4 — Excel value checklist. Empty = show all.
    startFrom: '',
    startTo: '',
    finishFrom: '',
    finishTo: '',
  });
  const [filterPanel, setFilterPanel] = useState({ open: false, col: null, x: 0, y: 0 });
  const [headerCtx, setHeaderCtx] = useState({ open: false, x: 0, y: 0 });
  // WI-S3 — rows hidden manually via row context menu; separate from
  // the filter path so filters can't accidentally un-hide them.
  const [hiddenRows, setHiddenRows] = useState(() => new Set());

  const reload = useCallback(async () => {
    // UDRAW-S5-A10 — cold vs silent. Cold reload is the very first
    // load of the page (no `view` yet); it drives the full-screen
    // "Loading…" branch. Every subsequent reload — live-event driven
    // or user-driven (Save handlers below use `onChanged={reload}`) —
    // is silent: the tree stays mounted, the drawer stays open with
    // its typed drafts intact, and only an unobtrusive "refreshing…"
    // dot in the header signals activity.
    const cold = !viewRef.current;
    if (cold) setLoading(true);
    else setRefreshing(true);
    if (cold) setError(null);
    try {
      const dto = await getDelivery(projectId);
      setView(dto);
      if (!cold) setError(null); // silent refresh recovered from a prior soft error
    } catch (err) {
      if (cold) {
        setError(readServerError(err));
      } else {
        // Silent refresh failure — keep the old view so the drawer
        // and everything the user has typed survives. Log to console
        // so the failure is diagnosable without a full-screen swap.
        // eslint-disable-next-line no-console
        console.warn('[UDRAW-S5] silent refresh failed:', readServerError(err));
      }
    } finally {
      if (cold) setLoading(false);
      else setRefreshing(false);
    }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  // UDRAW-S5 §2.2 — live layer wiring for the Delivery board.
  //   Connect + Join on mount; Leave + drop the subscription on unmount.
  //   deliveryChanged for THIS project → debounced (~300ms) reload().
  //   flashRowKey drives a 1.6s yellow highlight on the row that just
  //   changed (task if taskId present, otherwise WI). Falls back to
  //   no-flash when the payload omits both.
  const [flashRowKey, setFlashRowKey] = useState(null);
  const [presenceViewers, setPresenceViewers] = useState([]);
  const [fieldEditingMap, setFieldEditingMap] = useState({}); // key → { fullName, employeeId }
  const debounceRef = useRef(null);
  const flashTimerRef = useRef(null);
  useEffect(() => {
    if (!Number.isFinite(projectId) || projectId <= 0) return undefined;
    let cancelled = false;
    (async () => {
      await live.connect();
      if (cancelled) return;
      await live.joinProject(projectId);
    })();
    const off1 = live.on('deliveryChanged', (payload) => {
      if (!payload) return;
      // Reconnect notification uses projectId=null — trigger a full
      // reload regardless.
      if (payload.kind !== 'reconnect' && payload.projectId !== projectId) return;
      // Debounced reload — three fast events collapse into one refetch.
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        reload();
      }, 300);
      // Flash the row (visual only; UI test asserts on the state).
      const key = payload.taskId ? `t-${payload.taskId}`
        : payload.workItemId ? `wi-${payload.workItemId}` : null;
      if (key) {
        setFlashRowKey(key);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlashRowKey(null), 1600);
      }
    });
    const off2 = live.on('presenceChanged', (payload) => {
      if (!payload || payload.projectId !== projectId) return;
      setPresenceViewers(Array.isArray(payload.viewers) ? payload.viewers : []);
    });
    const off3 = live.on('fieldEditing', (payload) => {
      if (!payload || payload.projectId !== projectId) return;
      const anchor = payload.taskId != null ? `t-${payload.taskId}`
        : payload.workItemId != null ? `wi-${payload.workItemId}` : null;
      if (!anchor) return;
      const key = `${anchor}:${payload.fieldKey}`;
      setFieldEditingMap((m) => {
        const next = { ...m };
        if (payload.active) next[key] = { employeeId: payload.employeeId, fullName: payload.fullName };
        else delete next[key];
        return next;
      });
    });
    return () => {
      cancelled = true;
      // Defensive — a mocked liveClient in tests may not return a
      // callable unsub. In production `on` always returns a function.
      if (typeof off1 === 'function') off1();
      if (typeof off2 === 'function') off2();
      if (typeof off3 === 'function') off3();
      live.leaveProject(projectId);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [projectId, reload]);

  // UDRAW-S5-A7 — derived drawer targets. `drawerWi`, `rowDrawerTask`,
  // `rowDrawerWi` are recomputed on every view change; a live event
  // that mutates the item is reflected in the open drawer without any
  // manual copy-back. `null` when the item was deleted by a peer while
  // the drawer was open — the WorkItemDrawer's `missing` branch takes
  // over then (see JSX below).
  const drawerWi = useMemo(() => {
    if (drawerWiId == null || !view?.workItems) return null;
    return view.workItems.find((w) => w.id === drawerWiId) || null;
  }, [view, drawerWiId]);
  const rowDrawerTask = useMemo(() => {
    if (rowDrawer?.taskId == null || !view?.tasks) return null;
    return view.tasks.find((t) => t.id === rowDrawer.taskId) || null;
  }, [view, rowDrawer]);
  const rowDrawerWi = useMemo(() => {
    if (rowDrawer?.wiId == null || !view?.workItems) return null;
    return view.workItems.find((w) => w.id === rowDrawer.wiId) || null;
  }, [view, rowDrawer]);
  // UDRAW-S5 §2.2.3 — presence focus. Whenever the WI drawer or the
  // row drawer opens/closes, tell the hub where the caller is looking
  // so other viewers can see the aggregated focus in their presence
  // list. `null` clears the focus on close.
  useEffect(() => {
    if (!Number.isFinite(projectId) || projectId <= 0) return;
    let focus = null;
    if (drawerWiId != null) focus = { workItemId: drawerWiId };
    else if (rowDrawer?.taskId != null) focus = { taskId: rowDrawer.taskId };
    live.sendFocus(projectId, focus);
  }, [projectId, drawerWiId, rowDrawer]);

  // WI-S3.1 (MAJOR-3) — register outside-close on the NEXT tick so
  // the very click that opened the popover isn't the one that closes
  // it. mousedown fires before click, and the guard checks whether
  // the target is inside a popover surface (.ctx / .dlv-filter-panel).
  useEffect(() => {
    if (!ctxMenu.open) return;
    const close = (e) => {
      if (e.target.closest?.('.ctx')) return;
      setCtxMenu((c) => ({ ...c, open: false }));
    };
    const t = setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', close); };
  }, [ctxMenu.open]);
  useEffect(() => {
    if (!filterPanel.open) return;
    const close = (e) => {
      if (e.target.closest?.('.dlv-filter-panel')) return;
      setFilterPanel((p) => ({ ...p, open: false }));
    };
    const t = setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', close); };
  }, [filterPanel.open]);
  useEffect(() => {
    if (!headerCtx.open) return;
    const close = (e) => {
      if (e.target.closest?.('.ctx')) return;
      setHeaderCtx((c) => ({ ...c, open: false }));
    };
    const t = setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', close); };
  }, [headerCtx.open]);
  // WI-S3 — persona default filter. Fires once when the view lands
  // and the caller is a plain team member (not owner of any task,
  // not PM). Filter: state ∈ {New, Active} + assignee ∈ {me, unassigned}.
  // Skipped for owners / PMs so they see everything by default.
  const [personaApplied, setPersonaApplied] = useState(false);
  useEffect(() => {
    if (personaApplied || !view) return;
    const c = view.caller || {};
    const anyTaskOwned = view.tasks?.some((t) => t.ownerEmployeeId === c.employeeId);
    if (!c.isPm && !anyTaskOwned && c.employeeId != null) {
      setFilters((f) => ({
        ...f,
        states: new Set(['New', 'Active']),
        assignees: new Set([String(c.employeeId), 'null']),
      }));
    }
    setPersonaApplied(true);
  }, [view, personaApplied]);

  const workItemsByTask = useMemo(() => {
    const m = new Map();
    if (!view) return m;
    for (const w of view.workItems) {
      if (!m.has(w.projectTaskId)) m.set(w.projectTaskId, []);
      m.get(w.projectTaskId).push(w);
    }
    return m;
  }, [view]);

  // WI-S4.6 — defensive render. If a WI's projectTaskId doesn't match
  // any task in the payload it would silently vanish from the grid
  // and the "N WIs" header count would lie. Group them at the bottom
  // in an "Unanchored (data issue)" section so operators can see
  // exactly what needs cleaning up.
  const knownTaskIds = useMemo(() => {
    const s = new Set();
    for (const t of (view?.tasks || [])) s.add(t.id);
    return s;
  }, [view]);
  const orphanedWorkItems = useMemo(() => {
    if (!view) return [];
    return (view.workItems || []).filter((w) => !knownTaskIds.has(w.projectTaskId));
  }, [view, knownTaskIds]);

  // WI-S3 — filter helpers.
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const passesFilters = (wi) => {
    if (filters.states.size > 0 && !filters.states.has(wi.status)) return false;
    if (filters.lateOnly) {
      const late = wi.status !== 'Closed' && wi.plannedFinish
                   && new Date(wi.plannedFinish) < todayStart;
      if (!late) return false;
    }
    const akey = wi.assigneeEmployeeId == null ? 'null' : String(wi.assigneeEmployeeId);
    if (filters.assignees.size > 0 && !filters.assignees.has(akey)) return false;
    if (filters.titleText && !(wi.title || '').toLowerCase().includes(filters.titleText.toLowerCase())) return false;
    // WI-S4.4 — Excel value filter on Work Item column: if the user
    // has ticked a set of titles, only those pass.
    if (filters.titleValues.size > 0 && !filters.titleValues.has(wi.title || '')) return false;
    const s = wi.plannedStart ? new Date(wi.plannedStart) : null;
    const f = wi.plannedFinish ? new Date(wi.plannedFinish) : null;
    if (filters.startFrom && s && s < new Date(filters.startFrom)) return false;
    if (filters.startTo && s && s > new Date(filters.startTo)) return false;
    if (filters.finishFrom && f && f < new Date(filters.finishFrom)) return false;
    if (filters.finishTo && f && f > new Date(filters.finishTo)) return false;
    return true;
  };
  const anyFilterActive =
    filters.states.size > 0 || filters.lateOnly || filters.assignees.size > 0
    || filters.titleText || filters.titleValues.size > 0
    || filters.startFrom || filters.startTo
    || filters.finishFrom || filters.finishTo;
  // For a WI to render, EITHER itself or ANY descendant must pass.
  // Compute a "visibleWiIds" set with a bottom-up pass so parents of
  // matching descendants stay visible for context (spec §1).
  const visibleWiIds = useMemo(() => {
    if (!view) return new Set();
    const set = new Set();
    // A subtree map for descendant lookups.
    const kidsByParent = new Map();
    for (const w of view.workItems) {
      const k = w.parentWorkItemId ?? 0;
      if (!kidsByParent.has(k)) kidsByParent.set(k, []);
      kidsByParent.get(k).push(w);
    }
    const walk = (w) => {
      let anyChild = false;
      const kids = kidsByParent.get(w.id) || [];
      for (const k of kids) {
        if (walk(k)) anyChild = true;
      }
      const selfMatch = passesFilters(w);
      if (selfMatch || anyChild) {
        set.add(w.id);
        return true;
      }
      return false;
    };
    for (const root of (kidsByParent.get(0) || [])) walk(root);
    return set;
  }, [view, filters, todayStart]);   // eslint-disable-line react-hooks/exhaustive-deps

  // WI-S4.1 — the continuous ordinal `n` was carried through
  // render as a mutable `rowNumCtx.n` — a render-time side effect
  // that could inconsistently identify DOM nodes on trusted key
  // events under concurrent React (browser dispatches keydown/input
  // on the microtask boundary, which raced with the mid-render
  // mutation). We now compute the numbers up-front into a Map keyed
  // by row key, so the JSX pass reads a plain lookup — pure render.
  const rowNumberByKey = useMemo(() => {
    const m = new Map();
    if (!view) return m;
    const localTasks = view.tasks || [];
    const wisByTaskLocal = new Map();
    for (const w of (view.workItems || [])) {
      if (!wisByTaskLocal.has(w.projectTaskId)) wisByTaskLocal.set(w.projectTaskId, []);
      wisByTaskLocal.get(w.projectTaskId).push(w);
    }
    let n = 0;
    const walkWi = (wi) => {
      if (hiddenRows.has(wi.id)) return;
      if (anyFilterActive && !visibleWiIds.has(wi.id)) return;
      m.set(`wi-${wi.id}`, ++n);
      if (collapsed.has(wi.id)) return;
      const kids = (view.workItems || [])
        .filter((c) => c.parentWorkItemId === wi.id)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      for (const k of kids) walkWi(k);
    };
    // WI-S4.4 — same cascading rule the render loop uses: a task
    // whose ancestor is collapsed doesn't get a row number either
    // (else numbering skips real gaps or double-counts).
    const stack = []; // { level, collapsed }
    const cascadedHidden = new Set();
    for (const t of localTasks) {
      const level = t.outlineLevel || 0;
      while (stack.length && stack[stack.length - 1].level >= level) stack.pop();
      if (stack.some((a) => a.collapsed)) cascadedHidden.add(t.id);
      stack.push({ level, collapsed: collapsedTasks.has(t.id) });
    }
    for (const t of localTasks) {
      if (hiddenRows.has(`t-${t.id}`)) continue;
      if (cascadedHidden.has(t.id)) continue;
      m.set(`t-${t.id}`, ++n);
      if (collapsedTasks.has(t.id)) continue;
      const taskWis = (wisByTaskLocal.get(t.id) || []).filter((w) => w.parentWorkItemId == null);
      for (const w of taskWis) walkWi(w);
    }
    return m;
  }, [view, hiddenRows, anyFilterActive, visibleWiIds, collapsed, collapsedTasks]);

  // QLOG-S2 (UDRAW-S2) — row-click handler for a task row. Classifies
  // the row by (taskMode / hasChildTasks / taskWis.length) per spec
  // §2.1 and picks the right drawer mode:
  //   WBS row              → info(phase)
  //   summary (has kids)   → info(summary)
  //   costed task w/ WIs   → info(task-with-wis)
  //   leaf costed (no WIs) → quickLog
  // Each state also carries the raw task DTO so `buildDrawerTreeData`
  // can anchor the sticky HIERARCHY tree on it.
  // UDRAW-S5-A7 — this callback now stores only `taskId + infoKind`.
  // The actual `infoContent` object (banner / cards / childRows) is
  // rebuilt every render from the live `view` via `rowDrawerInfoContent`
  // below, so a live `deliveryChanged` refreshes the drawer's contents
  // (child task/WI list, rollups) in place.
  const openTaskRowDrawer = useCallback((t, taskWis, taskHasChildTasks) => {
    // WBS-RETIRE — the WBS `phase` infoKind is gone; every task
    // routes through summary / task-with-wis / quickLog.
    if (taskHasChildTasks) {
      setRowDrawer({ kind: 'info', taskId: t.id, infoKind: 'summary' });
      return;
    }
    if ((taskWis || []).length > 0) {
      setRowDrawer({ kind: 'info', taskId: t.id, infoKind: 'task-with-wis' });
      return;
    }
    // Leaf costed task with no WIs → quick log against the QLOG-S1 holder.
    setRowDrawer({ kind: 'quickLog', taskId: t.id });
  }, []);

  // UDRAW-S5-A7 — the info panel (banner / cards / child rows) is
  // rebuilt from `view` on every render. When a peer's write lands
  // (a new WI, a title change, a status flip) the panel reflects it
  // without any explicit copy-back. `null` here means the row drawer
  // is either not in info mode, or the anchoring task/WI has vanished
  // from view — the "missing" branch in the JSX below covers the
  // latter and shows the "no longer available" notice.
  const rowDrawerInfoContent = useMemo(() => {
    if (rowDrawer?.kind !== 'info') return null;
    if (rowDrawer.infoKind === 'wi-with-children') {
      if (!rowDrawerWi) return null;
      const kids = (view?.workItems || []).filter((w) => w.parentWorkItemId === rowDrawerWi.id);
      return buildWiInfoContent(rowDrawerWi, kids, ({ wi: child }) => {
        setRowDrawer(null);
        setDrawerWiId(child.id);
      });
    }
    const t = rowDrawerTask;
    if (!t) return null;
    if (rowDrawer.infoKind === 'phase') return buildPhaseInfoContent(t);
    if (rowDrawer.infoKind === 'summary') {
      return buildSummaryInfoContent(t, view?.tasks || [], ({ task: nextTask }) => {
        const nextWis = (view?.workItems || []).filter((w) => w.projectTaskId === nextTask.id);
        const list = view?.tasks || [];
        const idx = list.findIndex((x) => x.id === nextTask.id);
        const nx = list[idx + 1];
        const nextHasChildren = !!nx && (nx.outlineLevel || 0) > (nextTask.outlineLevel || 0);
        openTaskRowDrawer(nextTask, nextWis, nextHasChildren);
      });
    }
    if (rowDrawer.infoKind === 'task-with-wis') {
      const taskWis = (view?.workItems || []).filter((w) => w.projectTaskId === t.id);
      return buildTaskWithWisInfoContent(t, taskWis, ({ wi }) => {
        setRowDrawer(null);
        setDrawerWiId(wi.id);
      });
    }
    return null;
  }, [rowDrawer, rowDrawerTask, rowDrawerWi, view, openTaskRowDrawer]);

  // WI-S4.3 — structural task classification: task[i] has child tasks
  // iff task[i+1] exists AND its outlineLevel is deeper than task[i]'s.
  // Server ships tasks pre-sorted by SortOrder, so a single pass on
  // the flat list yields the right answer for any hierarchy depth and
  // any naming (no phase / sprint assumptions).
  const hasChildTasksById = useMemo(() => {
    const m = new Map();
    const list = view?.tasks || [];
    for (let i = 0; i < list.length; i++) {
      const cur = list[i];
      const next = list[i + 1];
      const level = cur.outlineLevel || 0;
      m.set(cur.id, !!next && (next.outlineLevel || 0) > level);
    }
    return m;
  }, [view]);

  // WI-S4.4 — cascading collapse. Collapsing a task hides its ENTIRE
  // subtree: every deeper-OutlineLevel task up to the next row at
  // same-or-shallower level, plus every WI anchored anywhere inside
  // it. Walks the pre-sorted flat task list with an ancestor stack.
  const cascadedHiddenTasks = useMemo(() => {
    const hidden = new Set();
    const list = view?.tasks || [];
    const stack = []; // { level, collapsed }
    for (const t of list) {
      const level = t.outlineLevel || 0;
      while (stack.length && stack[stack.length - 1].level >= level) stack.pop();
      if (stack.some((a) => a.collapsed)) hidden.add(t.id);
      stack.push({ level, collapsed: collapsedTasks.has(t.id) });
    }
    return hidden;
  }, [view, collapsedTasks]);

  // WI-S4.4 — Excel value list for the Work Item column filter. Union
  // of distinct task names + WI titles present in the grid, sorted
  // case-insensitively. Empty titles are excluded.
  const titleValueList = useMemo(() => {
    const s = new Set();
    for (const t of (view?.tasks || [])) if (t.name) s.add(t.name);
    for (const w of (view?.workItems || [])) if (w.title) s.add(w.title);
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [view]);

  // UDRAW-S5-A10 — only the COLD-START loading/error branches take
  // the page. A subsequent silent refresh (loading OR error while a
  // `view` already exists) never swaps the tree; the drawer + drafts
  // stay put.
  if (loading && !view) return <div className="pdp"><div style={{ padding: 40 }}>Loading…</div></div>;
  if (error && !view) return (
    <div className="pdp">
      <div style={{ padding: 40, color: '#a12a2a' }} data-testid="dlv-error">{error}</div>
    </div>
  );
  if (!view) return null;

  const { project, team, tasks, caller } = view;
  const canEditOwner = !!caller?.isPm;

  // UDRAW-S3 §2.1 — the ghost-based create flow is gone. WI creation
  // lives inside the drawer now (see WorkItemDrawer §2.2 and
  // TaskDrawerForm §2.2). Successful creates come back via
  // `onWorkItemCreated` on the two drawer instances and reload the
  // board with the fresh DTO in place.

  const doOwnerChange = async (taskId, employeeIdOrNull) => {
    try {
      await setTaskOwner(projectId, taskId, employeeIdOrNull);
      toast('Owner changed. Rights moved instantly.', { variant: 'success' });
      reload();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };

  const doQuickTakeOver = async (wiId) => {
    try {
      await takeOverWorkItem(wiId);
      toast("It's yours.", { variant: 'success' });
      reload();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };
  const doQuickStart = async (wiId) => {
    try {
      await startWorkItem(wiId);
      toast('Started.', { variant: 'success' });
      reload();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };
  const doQuickClose = async (wiId) => {
    try {
      await closeWorkItem(wiId, true);
      toast('Closed. For returned work, create a new work item.', { variant: 'success' });
      reload();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };

  const openCtx = (e, target) => {
    e.preventDefault();
    setCtxMenu({ open: true, x: e.clientX, y: e.clientY, target });
  };

  // WI-S3 — late helpers.
  const isLate = (wi) => wi.status !== 'Closed' && wi.plannedFinish
                        && new Date(wi.plannedFinish) < todayStart;
  const lateDays = (wi) => {
    if (!isLate(wi)) return 0;
    const finish = new Date(wi.plannedFinish);
    finish.setHours(0, 0, 0, 0);
    return Math.max(1, Math.round((todayStart - finish) / (24 * 3600 * 1000)));
  };
  const rollupLateChildren = (wi) => {
    // For a parent: count late open descendants (any depth).
    let count = 0;
    const kidsByParent = new Map();
    for (const w of view.workItems) {
      const k = w.parentWorkItemId ?? 0;
      if (!kidsByParent.has(k)) kidsByParent.set(k, []);
      kidsByParent.get(k).push(w);
    }
    const walk = (id) => {
      for (const k of kidsByParent.get(id) || []) {
        if (isLate(k)) count++;
        walk(k.id);
      }
    };
    walk(wi.id);
    return count;
  };
  // WI-S3 — Timeline SVG for a WI row (or task row). Draws:
  //   * plan bar (grey New / blue Active / green Closed)
  //   * red overdue segment (plannedFinish → today) for open late items
  //   * dashed today line
  // Fits in a ~180px cell. Range = [min(planStart, projectStart), max(planFinish, today) + 3d].
  const renderTimelineCell = (row, opts = {}) => {
    const {
      plannedStart, plannedFinish, status = 'New', isTask = false,
    } = opts;
    if (!plannedStart || !plannedFinish) {
      return <td className="dc" data-testid={`dlv-timeline-${row.id}`} />;
    }
    const W = 180; const H = 12;
    const s = new Date(plannedStart); s.setHours(0, 0, 0, 0);
    const f = new Date(plannedFinish); f.setHours(0, 0, 0, 0);
    const today = todayStart;
    const rangeMin = new Date(Math.min(s.getTime(), today.getTime() - 3 * 86400000));
    const rangeMax = new Date(Math.max(f.getTime(), today.getTime() + 3 * 86400000));
    const spanMs = Math.max(1, rangeMax - rangeMin);
    const xOf = (d) => Math.round(((d - rangeMin) / spanMs) * W);
    const x1 = xOf(s), x2 = xOf(f);
    const overdue = status !== 'Closed' && f < today;
    const overdueX2 = overdue ? xOf(today) : x2;
    const barColor = status === 'Closed' ? '#43a047' : status === 'Active' ? '#5b9bd5' : '#9e9e9e';
    return (
      <td className="dc" data-testid={`dlv-timeline-${row.id}`}
          title={`${plannedStart?.slice(0, 10)} → ${plannedFinish?.slice(0, 10)}`}>
        <svg width={W} height={H} style={{ verticalAlign: 'middle' }}>
          {isTask && (
            <rect x={x1} y={0} width={Math.max(1, x2 - x1)} height={H}
                  fill="#f5f5e8" stroke="#c0c8b0" strokeWidth={0.5} />
          )}
          {!isTask && (
            <rect x={x1} y={2} width={Math.max(1, x2 - x1)} height={H - 4}
                  fill={barColor} rx={2} />
          )}
          {overdue && !isTask && (
            <rect x={x2} y={2} width={Math.max(1, overdueX2 - x2)} height={H - 4}
                  fill="#c0392b" rx={2} />
          )}
          <line x1={xOf(today)} y1={0} x2={xOf(today)} y2={H}
                stroke="#333" strokeDasharray="2 2" />
        </svg>
      </td>
    );
  };

  // Render tree of work items under a task.
  const renderWorkItem = (task, wi, depth, isOwner, rows, filteredCounter) => {
    if (hiddenRows.has(wi.id)) return;
    if (anyFilterActive && !visibleWiIds.has(wi.id)) {
      // Bumped so the parent task can display the "N outside filter" summary.
      if (filteredCounter) filteredCounter.hidden++;
      if (filteredCounter && wi.status !== 'Closed') filteredCounter.open++;
      if (filteredCounter && isLate(wi)) filteredCounter.late++;
      return;
    }
    const isCollapsed = collapsed.has(wi.id);
    const kids = view.workItems
      .filter((c) => c.parentWorkItemId === wi.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const isAssignee = wi.assigneeEmployeeId === caller?.employeeId;
    const canAct = (isAssignee || isOwner) && wi.status !== 'Closed';
    const canTakeOver = wi.status !== 'Closed' && wi.assigneeEmployeeId == null && !isAssignee;

    // WI-S4.2 — Action buttons ride in PDP's .toolbar button visual
    // language (used as row-hover actions here, revealed via the
    // dlv-row-action class defined in DeliveryPage.css).
    let action = null;
    if (canTakeOver && wi.status === 'New' && (caller?.isPm || caller?.isTeamMember)) {
      action = (
        <button className="dlv-row-action"
                data-testid={`wi-row-takeover-start-${wi.id}`}
                // WI-S3.2 (C) — take-over already atomically moves
                // New → Active on the server (WorkItemService.TakeOverAsync).
                onClick={(e) => { e.stopPropagation(); doQuickTakeOver(wi.id); }}>
          Take over &amp; Start
        </button>
      );
    } else if (canAct && wi.status === 'New') {
      action = (
        <button className="dlv-row-action"
                data-testid={`wi-row-start-${wi.id}`}
                onClick={(e) => { e.stopPropagation(); doQuickStart(wi.id); }}>
          Start work
        </button>
      );
    } else if (canAct && wi.status === 'Active') {
      action = (
        <button className="dlv-row-action"
                data-testid={`wi-row-close-${wi.id}`}
                onClick={(e) => { e.stopPropagation(); doQuickClose(wi.id); }}>
          Close work item
        </button>
      );
    }

    const wiLateDays = kids.length > 0 ? 0 : lateDays(wi);
    const parentLateChildren = kids.length > 0 ? rollupLateChildren(wi) : 0;
    // WI-S4.3 — WI classification: aggregating iff this WI has child
    // WIs (bold sum row); leaf WIs are plain working rows. Depth for
    // indent walks the task's outline level first, then +1 to enter
    // the task's WI subtree, then the WI's own child depth.
    const wiHasChildren = kids.length > 0;
    const wiRowClass = wiHasChildren ? 'sum l1' : '';
    const taskLevel = task.outlineLevel || 0;
    const indentSteps = taskLevel + 1 + depth;
    rows.push(
      <tr key={`wi-${wi.id}`}
          className={wiRowClass}
          data-testid={`dlv-wi-row-${wi.id}`}
          onContextMenu={(e) => openCtx(e, { kind: 'wi', wi, task })}>
        <td className="rn" data-testid={`dlv-rn-wi-${wi.id}`}>{rowNumberByKey.get(`wi-${wi.id}`) ?? ''}</td>
        {colVis.state && (
          <td className="st"
              onClick={(e) => { e.stopPropagation();
                setCtxMenu({ open: true, x: e.clientX, y: e.clientY, target: { kind: 'state', wi, task } }); }}>
            <span className={`mi ${miClassFor(wi.status)}`}
                  data-testid={`dlv-wi-state-${wi.id}`}
                  title={wi.status}>{miLetterFor(wi.status)}</span>
          </td>
        )}
        <td className="tn"
            data-testid={`dlv-wi-title-${wi.id}`}
            onClick={() => {
              // QLOG-S2 — a WI with sub-items opens the read-only info
              // drawer with the list of its children (hours land on the
              // leaves). Leaf WIs keep the normal WorkItemDrawer path.
              if (wiHasChildren) {
                // UDRAW-S5-A7 — id-based; the info panel content is
                // rebuilt from `view` in `rowDrawerInfoContent`.
                setRowDrawer({ kind: 'info', wiId: wi.id, infoKind: 'wi-with-children' });
              } else {
                setDrawerWiId(wi.id);
              }
            }}>
          <span className="ind" style={{ width: indentSteps * 18 }} />
          {wiHasChildren ? (
            <span className="eb"
                  data-testid={`dlv-wi-toggle-${wi.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCollapsed((s) => {
                      const n = new Set(s);
                      if (n.has(wi.id)) n.delete(wi.id); else n.add(wi.id);
                      return n;
                    });
                  }}>{isCollapsed ? '►' : '▼'}</span>
          ) : (
            <span className="spacer" />
          )}
          <span className="tt">
            <span style={{ color: '#888', fontSize: 11, marginRight: 6 }}>WI-{wi.id}</span>
            {wi.title}
          </span>
          {wiLateDays > 0 && (
            <span className="cr-badge"
                  data-testid={`dlv-wi-late-${wi.id}`}
                  style={{ background: '#fdecea', color: '#c0392b', borderColor: '#e8a9a3' }}
                  title={`Planned finish ${gridDate(wi.plannedFinish)} · ${wiLateDays}d late`}>
              late {wiLateDays}d
            </span>
          )}
          {parentLateChildren > 0 && (
            <span className="cr-badge"
                  data-testid={`dlv-wi-late-parent-${wi.id}`}
                  style={{ background: '#fdecea', color: '#c0392b', borderColor: '#e8a9a3' }}>
              {parentLateChildren} late in subtree
            </span>
          )}
          {/* UDRAW-S3 §2.1 — "+ child" opens the WI drawer with the
              create form auto-expanded. Hidden on Closed and at
              depth 2 (child would exceed the 3-level cap). */}
          {isOwner && !isCollapsed && depth < 2 && wi.status !== 'Closed' && (
            <button
              className="dlv-row-add"
              data-testid={`dlv-wi-add-child-${wi.id}`}
              onClick={(e) => {
                e.stopPropagation();
                setAutoCreateRequest({ kind: 'wi', wiId: wi.id });
                setDrawerWiId(wi.id);
              }}>+ child</button>
          )}
          {action}
        </td>
        {colVis.assignee && (
          <td className="dc">
            {wi.assigneeFullName || <span style={{ color: '#aaa' }}>—</span>}
          </td>
        )}
        {colVis.start && (<td className="dc">{gridDate(wi.plannedStart)}</td>)}
        {colVis.finish && (<td className="dc">{gridDate(wi.plannedFinish)}</td>)}
        {colVis.own && (
          <td className="tch" data-testid={`dlv-wi-own-${wi.id}`}>{(wi.ownHours || 0).toFixed(1)}</td>
        )}
        {colVis.sigma && (
          <td className="tch"
              data-testid={`dlv-wi-rollup-${wi.id}`}>{(wi.rollupHours || 0).toFixed(1)}</td>
        )}
        {colVis.budget && (<td className="tch" />)}
        {colVis.timeline && renderTimelineCell(wi, {
          plannedStart: wi.plannedStart, plannedFinish: wi.plannedFinish, status: wi.status,
        })}
      </tr>
    );

    if (!isCollapsed) {
      for (const k of kids) renderWorkItem(task, k, depth + 1, isOwner, rows, filteredCounter);
    }
  };

  // WI-S3.2 (B) — ONE continuous ordinal across every visible row
  // (task rows and WI rows both bump the same counter, matching the
  // prototype and PDP). Ghost / filter-summary rows deliberately
  // don't take a number. WI-S4.1 — the counter is now built in a
  // pre-render useMemo (rowNumberByKey) so this JSX pass is pure.
  const rows = [];
  for (const t of tasks) {
    if (hiddenRows.has(`t-${t.id}`)) continue;
    // WI-S4.4 — cascading collapse: skip a task whose ancestor is
    // currently collapsed. Its WIs are skipped as a consequence (the
    // WI loop for THIS task doesn't run — but child tasks would have
    // rendered their WIs on their own iteration, so cut them here).
    if (cascadedHiddenTasks.has(t.id)) continue;
    const taskRn = rowNumberByKey.get(`t-${t.id}`) ?? '';
    const taskWis = (workItemsByTask.get(t.id) || []).filter((w) => w.parentWorkItemId == null);
    const isOwner = caller?.isPm || t.ownerEmployeeId === caller?.employeeId;
    const collapsedTask = collapsedTasks.has(t.id);
    // Task's commercial-window overdue chip: at least one WI in this
    // task is open past the task's planned end date.
    const taskEnd = t.plannedEndDate ? new Date(t.plannedEndDate) : null;
    const taskOpenPastCommercial = taskEnd && taskEnd < todayStart
      ? (workItemsByTask.get(t.id) || []).filter((w) => w.status !== 'Closed').length
      : 0;

    // WI-S4.3 — structural row classification. Summary iff this task
    // has a deeper subtask; otherwise plain white. Depth class picks
    // the PDP tone (sum.l0 top / sum.l1 deeper).
    const taskLevel = t.outlineLevel || 0;
    const taskHasChildTasks = hasChildTasksById.get(t.id) === true;
    const taskRowClass = taskHasChildTasks
      ? (taskLevel === 0 ? 'sum l0' : 'sum l1')
      : '';
    rows.push(
      <tr key={`t-${t.id}`}
          className={taskRowClass}
          data-testid={`dlv-task-row-${t.id}`}
          onContextMenu={(e) => openCtx(e, { kind: 'task', task: t })}>
        <td className="rn">{taskRn}</td>
        {colVis.state && (<td className="st" />)}
        <td className="tn"
            data-testid={`dlv-task-title-${t.id}`}
            onClick={() => openTaskRowDrawer(t, taskWis, taskHasChildTasks)}>
          <span className="ind" style={{ width: taskLevel * 18 }} />
          {(taskHasChildTasks || taskWis.length > 0) ? (
            <span className="eb"
                  data-testid={`dlv-task-toggle-${t.id}`}
                  onClick={(e) => {
                    e.stopPropagation();  // QLOG-S2 — toggle doesn't open drawer
                    setCollapsedTasks((s) => {
                      const n = new Set(s);
                      if (n.has(t.id)) n.delete(t.id); else n.add(t.id);
                      return n;
                    });
                  }}>{collapsedTask ? '►' : '▼'}</span>
          ) : (
            <span className="spacer" />
          )}
          <span className="tt">{t.name}</span>
          {t.ownerFullName && (
            <span
              data-testid={`dlv-task-owner-inline-${t.id}`}
              onContextMenu={(e) => { if (canEditOwner) openCtx(e, { kind: 'taskOwner', task: t }); }}
              title={canEditOwner ? 'Right-click to change owner' : 'Owner'}
              style={{ marginLeft: 8, fontSize: 11, color: '#666' }}>
              ★ {t.ownerFullName}
            </span>
          )}
          {taskOpenPastCommercial > 0 && (
            <span className="cr-badge"
                  data-testid={`dlv-task-late-${t.id}`}
                  style={{ background: '#fdecea', color: '#c0392b', borderColor: '#e8a9a3' }}
                  title={`Commercial finish ${isoDate(t.plannedEndDate)} is past`}>
              {taskOpenPastCommercial} open past {isoDate(t.plannedEndDate)}
            </span>
          )}
          {/* WBS-RETIRE — WBS filter removed; every non-collapsed
              costed row shows the "+ work item" affordance to owners. */}
          {isOwner && !collapsedTask && (
            <button
              className="dlv-row-add"
              data-testid={`dlv-task-add-wi-${t.id}`}
              onClick={(e) => {
                e.stopPropagation();
                // UDRAW-S3 §2.1 — open the task drawer with the
                // create form auto-expanded. The row-drawer path
                // handles the mode dispatch (task-with-wis /
                // quickLog) but treats create as an override.
                const taskWis = workItemsByTask.get(t.id) || [];
                const taskHasChildTasks = hasChildTasksById.get(t.id) === true;
                setAutoCreateRequest({ kind: 'task', taskId: t.id });
                openTaskRowDrawer(t, taskWis, taskHasChildTasks);
              }}>+ work item</button>
          )}
        </td>
        {colVis.assignee && (
          <td
            className="dc"
            data-testid={`dlv-task-owner-${t.id}`}
            // OWN-FIX — the Assignee cell IS the entry point to the
            // existing kind='taskOwner' menu when the caller is PM.
            // WBS-RETIRE — the WBS carve-out is gone; every row is a
            // costed task and eligible for owner assignment.
            onClick={(e) => { if (canEditOwner) openCtx(e, { kind: 'taskOwner', task: t }); }}
            onContextMenu={(e) => { if (canEditOwner) openCtx(e, { kind: 'taskOwner', task: t }); }}
            style={canEditOwner ? { cursor: 'pointer' } : undefined}
          >
            {t.ownerFullName
              ? <span title={canEditOwner ? 'Click to change owner' : 'Owner'}>★ {t.ownerFullName}</span>
              : (canEditOwner
                ? <span
                    data-testid={`dlv-task-owner-assign-${t.id}`}
                    title="Assign a theme owner"
                    style={{ color: '#999', cursor: 'pointer' }}
                  >+ Assign owner</span>
                : <span style={{ color: '#999' }}>—</span>)}
          </td>
        )}
        {colVis.start && (<td className="dc">{gridDate(t.plannedStartDate)}</td>)}
        {colVis.finish && (<td className="dc">{gridDate(t.plannedEndDate)}</td>)}
        {colVis.own && (<td className="tch" />)}
        {colVis.sigma && (
          <td className="tch" data-testid={`dlv-task-logged-${t.id}`}>
            {(t.loggedHours || 0).toFixed(1)}
          </td>
        )}
        {colVis.budget && (
          <td className="tch" data-testid={`dlv-task-budget-${t.id}`}>
            {(t.budgetHours || 0).toFixed(1)}
            {t.budgetHours > 0 && (
              <span data-testid={`dlv-task-burn-${t.id}`}
                    style={{
                      marginLeft: 6, fontSize: 11,
                      color: (t.loggedHours || 0) > t.budgetHours ? '#c0392b' : '#555',
                    }}>
                ({Math.round(((t.loggedHours || 0) / t.budgetHours) * 100)}%)
              </span>
            )}
          </td>
        )}
        {colVis.timeline && renderTimelineCell(t, {
          plannedStart: t.plannedStartDate, plannedFinish: t.plannedEndDate,
          status: 'Active', isTask: true,
        })}
      </tr>
    );

    // Track visible WI rows per outer loop to decide the empty-state
    // row below. WI-S4.6 — Enter's cascading-collapse gate above may
    // hide all WIs; we count from a memo already computed for the
    // filter path (visibleWiIds) rather than tallying inside the JSX.
    if (!collapsedTask) {
      const counter = { hidden: 0, open: 0, late: 0 };
      for (const w of taskWis) renderWorkItem(t, w, 0, isOwner, rows, counter);
      if (counter.hidden > 0 && anyFilterActive) {
        // "N items outside the filter · X open · Y late" summary row.
        rows.push(
          <tr key={`sum-${t.id}`}
              data-testid={`dlv-filter-summary-${t.id}`}
              style={{ background: '#fafafa', fontStyle: 'italic', color: '#666' }}>
            <td className="rn" />
            <td colSpan={99} style={{ padding: '3px 8px' }}>
              {counter.hidden} items outside the filter · {counter.open} open · {counter.late} late
            </td>
          </tr>
        );
      }

      // UDRAW-S3 §2.1 — ghost row removed. Creation moved to the
      // drawer form. The "+ work item" / "+ child" buttons now open
      // the drawer with the create form auto-expanded.
    }
  }

  // WI-S4.6 — orphaned WI group. Rendered as its own bordered block
  // at the end of the grid so counts never lie and operators can see
  // which WIs need a re-anchor via the migration cleanup path.
  if (orphanedWorkItems.length > 0) {
    rows.push(
      <tr key="orphan-header"
          data-testid="dlv-orphan-header"
          style={{ background: '#fff3c2', color: '#856404', fontWeight: 700 }}>
        <td className="rn" />
        <td colSpan={99} style={{ padding: '4px 8px', borderTop: '2px solid #e6c34a' }}>
          Unanchored (data issue) — {orphanedWorkItems.length} WI{orphanedWorkItems.length === 1 ? '' : 's'} without a valid anchor task. Run the delivery migration cleanup to reassign or remove.
        </td>
      </tr>
    );
    for (const w of orphanedWorkItems) {
      rows.push(
        <tr key={`orphan-${w.id}`}
            data-testid={`dlv-orphan-row-${w.id}`}
            style={{ background: '#fffbea' }}>
          <td className="rn" />
          {colVis.state && (
            <td className="st">
              <span className={`mi ${miClassFor(w.status)}`} title={w.status}>
                {miLetterFor(w.status)}
              </span>
            </td>
          )}
          <td className="tn">
            <span className="spacer" />
            <span className="tt">
              <span style={{ color: '#888', fontSize: 11, marginRight: 6 }}>WI-{w.id}</span>
              {w.title}
            </span>
            <span className="cr-badge"
                  style={{ background: '#fdecea', color: '#c0392b', borderColor: '#e8a9a3' }}
                  title={`Anchor projectTaskId=${w.projectTaskId} is not on this project.`}>
              orphan · anchor {w.projectTaskId}
            </span>
          </td>
          {colVis.assignee && (
            <td className="dc">{w.assigneeFullName || <span style={{ color: '#aaa' }}>—</span>}</td>
          )}
          {colVis.start && (<td className="dc">{gridDate(w.plannedStart)}</td>)}
          {colVis.finish && (<td className="dc">{gridDate(w.plannedFinish)}</td>)}
          {colVis.own && (<td className="tch">{(w.ownHours || 0).toFixed(1)}</td>)}
          {colVis.sigma && (<td className="tch">{(w.rollupHours || 0).toFixed(1)}</td>)}
          {colVis.budget && (<td className="tch" />)}
          {colVis.timeline && (<td className="dc" />)}
        </tr>
      );
    }
  }

  // WI-S4.6 — empty-state row: active filters + zero visible anchored
  // WIs. Orphans are shown in their own group above and don't count.
  // We compute visibility from visibleWiIds (bottom-up filter walk) so
  // this stays in sync with what renderWorkItem actually emits.
  const filteredZero = anyFilterActive && (view?.workItems || [])
    .filter((w) => knownTaskIds.has(w.projectTaskId) && !hiddenRows.has(w.id))
    .every((w) => !visibleWiIds.has(w.id));
  if (filteredZero) {
    rows.push(
      <tr key="empty-state" data-testid="dlv-empty-state"
          style={{ background: '#fafafa', color: '#666' }}>
        <td className="rn" />
        <td colSpan={99} style={{ padding: '8px 12px', fontStyle: 'italic' }}>
          No work items match the filters.{' '}
          <button
            data-testid="dlv-empty-clear-filters"
            onClick={() => setFilters({
              states: new Set(), lateOnly: false, assignees: new Set(),
              titleText: '', titleValues: new Set(),
              startFrom: '', startTo: '', finishFrom: '', finishTo: '',
            })}
            style={{ background: 'transparent', border: 'none', color: '#4a90d9',
                     cursor: 'pointer', fontSize: 12, textDecoration: 'underline',
                     padding: 0, marginLeft: 4 }}>
            Clear all filters
          </button>
        </td>
      </tr>
    );
  }

  // UDRAW-S5 §2.2 — expose live state via data-attributes so
  // integration tests can assert the debounced refetch fired, which
  // row got flashed, and which fields other users are editing. The
  // rendered UI is elsewhere (presence chip in the header, per-drawer
  // indicators handed down via the content objects); these attrs are
  // deliberately test-oriented and cost near-zero.
  const fieldEditingKeys = Object.keys(fieldEditingMap).join(',');

  // UDRAW-S5.1 §2.2.4 — slice the global fieldEditing map to the
  // anchor a given drawer holds (wi-{id} or t-{id}). Values map from
  // `fieldKey` → `{ employeeId, fullName }` so drawers can render
  // <FieldEditingIndicator/> next to each label without touching the
  // hub layer themselves. Self-edits are dropped so the caller never
  // sees their own indicator on their own field.
  const liveEditingFor = (anchor) => {
    const scoped = {};
    const prefix = anchor + ':';
    for (const key of Object.keys(fieldEditingMap)) {
      if (!key.startsWith(prefix)) continue;
      const peer = fieldEditingMap[key];
      if (!peer || peer.employeeId === caller?.employeeId) continue;
      scoped[key.slice(prefix.length)] = peer;
    }
    return scoped;
  };
  return (
    <div className="pdp pdp-delivery" data-testid="delivery-page"
         data-live-flash={flashRowKey || ''}
         data-live-editing={fieldEditingKeys}>
      <div className="project-header">
        {/* QLOG-S2.1 §A.2 — the old one-way "Plan (read-only)" link is
            replaced by a segmented Delivery ↔ Plan toggle so switching
            between the two views doesn't have to go through the list.
            "← My Projects" stays as the back-to-list affordance. */}
        <span className="back" onClick={() => nav('/my-projects')} data-testid="dlv-back">← My Projects</span>
        <h1>{project.name}</h1>
        <span className="chip">{project.clientName || ''}</span>
        <ProjectViewToggle projectId={project.id} active="delivery" />
        {/* UDRAW-S5 §2.2.3 — "N online" chip fed by the live layer's
            presenceChanged snapshot. Silent when nobody else is
            watching; also silent when the live client couldn't
            connect (empty viewers list). */}
        {presenceViewers.length > 0 && (
          <span
            data-testid="dlv-live-presence"
            style={{
              marginLeft: 12, fontSize: 12, color: '#2a6b2a',
              background: '#e9f7ee', border: '1px solid #b6dcc3',
              borderRadius: 12, padding: '2px 10px',
            }}
            title={presenceViewers.map((v) => v.fullName).join(', ')}
          >{presenceViewers.length} online</span>
        )}
        {/* UDRAW-S5-A10 — silent-refresh indicator. Rendered only
            while a live-driven (or explicit onChanged) reload is in
            flight AND we already have a view (so it never overlaps
            the cold-start "Loading…" branch). Deliberately tiny — a
            single dot — so it doesn't fight for attention with the
            board or the drawer. Stable data-testid gives the fence
            tests a signal to assert on. */}
        {refreshing && (
          <span
            data-testid="dlv-refreshing"
            title="Refreshing…"
            style={{
              marginLeft: 8, fontSize: 12, color: '#8899a6',
              display: 'inline-block', width: 8, height: 8,
              borderRadius: 4, background: '#8899a6',
            }}
          />
        )}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#666' }}>
          {caller?.isPm ? 'PM' : caller?.isTeamMember ? 'Team' : ''}
        </div>
      </div>

      <div className="toolbar">
        <button
          data-testid="dlv-expand-all"
          onClick={() => { setCollapsedTasks(new Set()); setCollapsed(new Set()); }}>
          Expand All
        </button>
        <button
          data-testid="dlv-collapse-all"
          onClick={() => setCollapsedTasks(new Set(tasks.map((t) => t.id)))}>
          Collapse All
        </button>
        <span className="sep" />
        <span className="hint">Work items live under costed tasks. Comments always available.</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#888' }}>
          {tasks.length} tasks · {view.workItems.length} WIs
        </span>
      </div>

      <div className="grid-wrap">
        <table>
          <thead onContextMenu={(e) => { e.preventDefault(); setHeaderCtx({ open: true, x: e.clientX, y: e.clientY }); }}>
            <tr>
              <th>#</th>
              {colVis.state && (
                <th data-testid="dlv-th-state">
                  State
                  <span data-testid="dlv-funnel-state"
                        style={{ marginLeft: 4, cursor: 'pointer',
                                 color: filters.states.size > 0 || filters.lateOnly ? '#4a90d9' : '#999' }}
                        onClick={(e) => { e.stopPropagation();
                          setFilterPanel({ open: true, col: 'state', x: e.clientX, y: e.clientY }); }}>
                    ▼
                  </span>
                </th>
              )}
              <th data-testid="dlv-th-title">
                Work Item
                <span data-testid="dlv-funnel-title"
                      style={{ marginLeft: 4, cursor: 'pointer',
                               color: (filters.titleText || filters.titleValues.size > 0) ? '#4a90d9' : '#999' }}
                      onClick={(e) => { e.stopPropagation();
                        setFilterPanel({ open: true, col: 'title', x: e.clientX, y: e.clientY }); }}>
                  ▼
                </span>
              </th>
              {colVis.assignee && (
                <th data-testid="dlv-th-assignee">
                  Assignee
                  <span data-testid="dlv-funnel-assignee"
                        style={{ marginLeft: 4, cursor: 'pointer',
                                 color: filters.assignees.size > 0 ? '#4a90d9' : '#999' }}
                        onClick={(e) => { e.stopPropagation();
                          setFilterPanel({ open: true, col: 'assignee', x: e.clientX, y: e.clientY }); }}>
                    ▼
                  </span>
                </th>
              )}
              {colVis.start && (
                <th data-testid="dlv-th-start">
                  Start
                  <span data-testid="dlv-funnel-start"
                        style={{ marginLeft: 4, cursor: 'pointer',
                                 color: filters.startFrom || filters.startTo ? '#4a90d9' : '#999' }}
                        onClick={(e) => { e.stopPropagation();
                          setFilterPanel({ open: true, col: 'start', x: e.clientX, y: e.clientY }); }}>▼</span>
                </th>
              )}
              {colVis.finish && (
                <th data-testid="dlv-th-finish">
                  Finish
                  <span data-testid="dlv-funnel-finish"
                        style={{ marginLeft: 4, cursor: 'pointer',
                                 color: filters.finishFrom || filters.finishTo ? '#4a90d9' : '#999' }}
                        onClick={(e) => { e.stopPropagation();
                          setFilterPanel({ open: true, col: 'finish', x: e.clientX, y: e.clientY }); }}>▼</span>
                </th>
              )}
              {colVis.own && (<th>Own h</th>)}
              {colVis.sigma && (<th>Σ h</th>)}
              {colVis.budget && (<th>Budget h</th>)}
              {colVis.timeline && (<th>Timeline</th>)}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>

      {ctxMenu.open && (
        <div className="ctx" style={{ left: ctxMenu.x, top: ctxMenu.y }}
             data-testid="dlv-ctx-menu"
             onClick={(e) => e.stopPropagation()}>
          {ctxMenu.target?.kind === 'wi' && (
            <>
              <div className="ci"
                   data-testid="dlv-ctx-open"
                   onClick={() => { setDrawerWiId(ctxMenu.target.wi.id); setCtxMenu((c) => ({ ...c, open: false })); }}>
                Open
              </div>
              {/* WI-S3.1 MINOR-6 — Closed parent has no Add child. */}
              {(caller?.isPm || ctxMenu.target.task.ownerEmployeeId === caller?.employeeId)
                && ctxMenu.target.wi.status !== 'Closed' && (
                <div className="ci"
                     data-testid="dlv-ctx-add-child"
                     onClick={() => {
                       // UDRAW-S3 §2.1 — open the WI drawer with the
                       // create form auto-expanded on this parent.
                       setAutoCreateRequest({ kind: 'wi', wiId: ctxMenu.target.wi.id });
                       setDrawerWiId(ctxMenu.target.wi.id);
                       setCtxMenu((c) => ({ ...c, open: false }));
                     }}>
                  Add child
                </div>
              )}
              {ctxMenu.target.wi.assigneeEmployeeId == null
                && ctxMenu.target.wi.status !== 'Closed'
                && (caller?.isPm || caller?.isTeamMember) && (
                <div className="ci"
                     data-testid="dlv-ctx-takeover"
                     onClick={() => { doQuickTakeOver(ctxMenu.target.wi.id); setCtxMenu((c) => ({ ...c, open: false })); }}>
                  Take over
                </div>
              )}
            </>
          )}
          {/* ASGN-S3 §B.1 — the "Add work item" affordance was
              PM/owner-only pre-S3. It now surfaces for any team
              member on a costed (non-WBS) task; the underlying
              CreateAsync gives a non-PM/non-owner caller a fresh
              Active + self-assigned WI, which is exactly what a
              consultant creating a WI from the board expects. */}
          {ctxMenu.target?.kind === 'task'
            && (caller?.isPm
                || ctxMenu.target.task.ownerEmployeeId === caller?.employeeId
                || caller?.isTeamMember) && (
            // WBS-RETIRE — WBS taskMode filter removed; every task
            // shows the "Add work item" entry.
              <div className="ci"
                   data-testid="dlv-ctx-add-wi"
                   onClick={() => {
                     // UDRAW-S3 §2.1 — open the task drawer with the
                     // create form auto-expanded.
                     const t = ctxMenu.target.task;
                     const taskWis = workItemsByTask.get(t.id) || [];
                     const taskHasChildTasks = hasChildTasksById.get(t.id) === true;
                     setAutoCreateRequest({ kind: 'task', taskId: t.id });
                     openTaskRowDrawer(t, taskWis, taskHasChildTasks);
                     setCtxMenu((c) => ({ ...c, open: false }));
                   }}>
                Add work item
              </div>
          )}
          {/* WI-S4.2 — State cell menu: ADO forward-only transitions
              (N→A, A→C, C→nothing). Mirrors the plan grid Status .mi
              click menu so the two screens read as one product. */}
          {ctxMenu.target?.kind === 'state' && (
            <>
              {ctxMenu.target.wi.status === 'New' && (
                <div className="ci"
                     data-testid="dlv-ctx-state-start"
                     onClick={() => { doQuickStart(ctxMenu.target.wi.id); setCtxMenu((c) => ({ ...c, open: false })); }}>
                  Start work (N → A)
                </div>
              )}
              {ctxMenu.target.wi.status === 'Active' && (
                <div className="ci"
                     data-testid="dlv-ctx-state-close"
                     onClick={() => { doQuickClose(ctxMenu.target.wi.id); setCtxMenu((c) => ({ ...c, open: false })); }}>
                  Close work item (A → C)
                </div>
              )}
              {ctxMenu.target.wi.status === 'Closed' && (
                <div className="ci" style={{ color: '#999' }}
                     data-testid="dlv-ctx-state-noop">
                  Closed — create a new WI for returned work
                </div>
              )}
            </>
          )}
          {/* WI-S4.2 — Task Owner menu: PM-only, right-click on the
              "★ name" text on the task row. Keeps owner change out of
              the row body (no permanent select). */}
          {ctxMenu.target?.kind === 'taskOwner'
            && (caller?.isPm || ctxMenu.target.task.ownerEmployeeId === caller?.employeeId) && (
              <>
                <div className="ci" style={{ color: '#888', cursor: 'default' }}>
                  Owner →
                </div>
                <div className="ci"
                     data-testid="dlv-ctx-owner-none"
                     onClick={() => { doOwnerChange(ctxMenu.target.task.id, null); setCtxMenu((c) => ({ ...c, open: false })); }}>
                  — none —
                </div>
                {team.filter((m) => !m.isFormer).map((m) => (   // CEV-S3 — people who have left are not offered
                  <div key={m.employeeId} className="ci"
                       data-testid={`dlv-ctx-owner-${m.employeeId}`}
                       onClick={() => { doOwnerChange(ctxMenu.target.task.id, m.employeeId); setCtxMenu((c) => ({ ...c, open: false })); }}>
                    {m.fullName}
                  </div>
                ))}
              </>
          )}
          {/* WI-S3 — Hide row (and its subtree). Manual hides ignore
              filters — a re-shown row only comes back via "Unhide all". */}
          {ctxMenu.target?.kind === 'wi' && (
            <div className="ci"
                 data-testid="dlv-ctx-hide-row"
                 onClick={() => {
                   setHiddenRows((s) => {
                     const n = new Set(s); n.add(ctxMenu.target.wi.id); return n;
                   });
                   setCtxMenu((c) => ({ ...c, open: false }));
                 }}>
              Hide row (with subtree)
            </div>
          )}
          {ctxMenu.target?.kind === 'task' && (
            <div className="ci"
                 data-testid="dlv-ctx-hide-task"
                 onClick={() => {
                   setHiddenRows((s) => {
                     const n = new Set(s); n.add(`t-${ctxMenu.target.task.id}`); return n;
                   });
                   setCtxMenu((c) => ({ ...c, open: false }));
                 }}>
              Hide row
            </div>
          )}
        </div>
      )}

      {/* WI-S3 — header right-click context menu. */}
      {headerCtx.open && (
        <div className="ctx" style={{ left: headerCtx.x, top: headerCtx.y }}
             data-testid="dlv-header-ctx"
             onClick={(e) => e.stopPropagation()}>
          <div className="ci" data-testid="dlv-hctx-collapse-all"
               onClick={() => { setCollapsedTasks(new Set(tasks.map((t) => t.id))); setHeaderCtx({ open: false, x: 0, y: 0 }); }}>
            Collapse all
          </div>
          <div className="ci" data-testid="dlv-hctx-expand-all"
               onClick={() => { setCollapsedTasks(new Set()); setCollapsed(new Set()); setHeaderCtx({ open: false, x: 0, y: 0 }); }}>
            Expand all
          </div>
          <div className="cs" />
          <div className="ci" data-testid="dlv-hctx-unhide-rows"
               onClick={() => { setHiddenRows(new Set()); setHeaderCtx({ open: false, x: 0, y: 0 }); }}>
            Unhide all rows ({hiddenRows.size})
          </div>
          <div className="ci" data-testid="dlv-hctx-clear-filters"
               onClick={() => {
                 setFilters({
                   states: new Set(), lateOnly: false, assignees: new Set(),
                   titleText: '', titleValues: new Set(),
                   startFrom: '', startTo: '', finishFrom: '', finishTo: '',
                 });
                 setHeaderCtx({ open: false, x: 0, y: 0 });
               }}>
            Clear all filters
          </div>
          <div className="cs" />
          {/* Hide/Show columns */}
          {Object.entries(colVis).map(([k, on]) => (
            <div key={k} className="ci"
                 data-testid={`dlv-hctx-col-${k}`}
                 onClick={() => { setColVis((s) => ({ ...s, [k]: !on })); }}>
              {on ? '✓ Hide' : '＋ Show'} {k[0].toUpperCase() + k.slice(1)}
            </div>
          ))}
        </div>
      )}

      {/* WI-S3 — Excel-style filter popover panel. */}
      {filterPanel.open && (
        <div className="ctx dlv-filter-panel"
             style={{
               left: Math.min(filterPanel.x, (window.innerWidth || 1200) - 300),
               top: Math.min(filterPanel.y, (window.innerHeight || 800) - 300),
               padding: 8, minWidth: 220,
             }}
             data-testid={`dlv-filter-panel-${filterPanel.col}`}>
          {filterPanel.col === 'state' && (
            <>
              {['New', 'Active', 'Closed'].map((s) => (
                <label key={s} style={{ display: 'block', fontSize: 12, padding: '2px 0' }}>
                  <input type="checkbox"
                         data-testid={`dlv-filter-state-${s}`}
                         checked={filters.states.has(s)}
                         onChange={(e) => {
                           setFilters((f) => {
                             const n = new Set(f.states);
                             if (e.target.checked) n.add(s); else n.delete(s);
                             return { ...f, states: n };
                           });
                         }} /> {s}
                </label>
              ))}
              <label style={{ display: 'block', fontSize: 12, padding: '4px 0', borderTop: '1px solid #eee', marginTop: 4 }}>
                <input type="checkbox"
                       data-testid="dlv-filter-late-only"
                       checked={filters.lateOnly}
                       onChange={(e) => setFilters((f) => ({ ...f, lateOnly: e.target.checked }))} /> late only
              </label>
              <button data-testid="dlv-filter-clear-state"
                      onClick={() => setFilters((f) => ({ ...f, states: new Set(), lateOnly: false }))}
                      style={{ marginTop: 6, fontSize: 11 }}>Clear</button>
            </>
          )}
          {filterPanel.col === 'title' && (
            <TitleFilterPanel
              values={titleValueList}
              titleText={filters.titleText}
              titleValues={filters.titleValues}
              onTitleTextChange={(t) => setFilters((f) => ({ ...f, titleText: t }))}
              onValuesChange={(vs) => setFilters((f) => ({ ...f, titleValues: vs }))}
            />
          )}
          {filterPanel.col === 'assignee' && (
            <AssigneeFilterPanel team={team}
                                 selected={filters.assignees}
                                 onChange={(next) => setFilters((f) => ({ ...f, assignees: next }))} />
          )}
          {filterPanel.col === 'start' && (
            <>
              <div style={{ fontSize: 11, color: '#666' }}>Start ≥</div>
              <input type="date" data-testid="dlv-filter-start-from"
                     value={filters.startFrom}
                     onChange={(e) => setFilters((f) => ({ ...f, startFrom: e.target.value }))}
                     style={{ width: '100%', padding: 4 }} />
              <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>Start ≤</div>
              <input type="date" data-testid="dlv-filter-start-to"
                     value={filters.startTo}
                     onChange={(e) => setFilters((f) => ({ ...f, startTo: e.target.value }))}
                     style={{ width: '100%', padding: 4 }} />
              <button data-testid="dlv-filter-clear-start"
                      onClick={() => setFilters((f) => ({ ...f, startFrom: '', startTo: '' }))}
                      style={{ marginTop: 6, fontSize: 11 }}>Clear</button>
            </>
          )}
          {filterPanel.col === 'finish' && (
            <>
              <div style={{ fontSize: 11, color: '#666' }}>Finish ≥</div>
              <input type="date" data-testid="dlv-filter-finish-from"
                     value={filters.finishFrom}
                     onChange={(e) => setFilters((f) => ({ ...f, finishFrom: e.target.value }))}
                     style={{ width: '100%', padding: 4 }} />
              <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>Finish ≤</div>
              <input type="date" data-testid="dlv-filter-finish-to"
                     value={filters.finishTo}
                     onChange={(e) => setFilters((f) => ({ ...f, finishTo: e.target.value }))}
                     style={{ width: '100%', padding: 4 }} />
              <button data-testid="dlv-filter-clear-finish"
                      onClick={() => setFilters((f) => ({ ...f, finishFrom: '', finishTo: '' }))}
                      style={{ marginTop: 6, fontSize: 11 }}>Clear</button>
            </>
          )}
        </div>
      )}

      <WorkItemDrawer
        open={drawerWiId != null}
        onClose={() => setDrawerWiId(null)}
        wi={drawerWi}
        // UDRAW-S5-A7 — item deleted by a peer while the drawer was
        // open. The drawer renders a non-blocking "no longer available"
        // notice instead of the form; the viewer decides when to close.
        missing={drawerWiId != null && drawerWi == null}
        task={drawerWi ? tasks.find((t) => t.id === drawerWi.projectTaskId) : null}
        team={team}
        caller={caller}
        // UDRAW-S3.1 HOTFIX — DeliveryWorkItemDto does NOT carry
        // ProjectId, so CreateWorkItemForm was building
        // `/api/projects/undefined/work-items` (→ 404) on the child
        // create path. Pass the route projectId explicitly.
        projectId={view?.projectId ?? Number(id)}
        // UDRAW-S5.1 §2.2.4 — peer-editing map scoped to THIS WI's
        // anchor, so the drawer renders indicators next to fields.
        liveEditing={drawerWi ? liveEditingFor(`wi-${drawerWi.id}`) : null}
        hasChildren={!!drawerWi && view.workItems.some((c) => c.parentWorkItemId === drawerWi.id)}
        onChanged={reload}
        // UDRAW-S1 §3-4 (UDRAW-S2 shape) — pre-flatten the current WI's
        // phase sub-tree (phase → tasks → WIs → sub-WIs). Anchor is the
        // WI here; task drawers below anchor on the task. Clicks route
        // back through the existing row-click dispatch.
        treeData={buildDrawerTreeData({ wi: drawerWi }, view, workItemsByTask)}
        onTreeNavigate={(node) => {
          if (!node) return;
          if (node.kind === 'wi' && node.wi) {
            setDrawerWiId(node.wi.id);
            return;
          }
          if ((node.kind === 'task' || node.kind === 'phase') && node.task) {
            const t = node.task;
            const taskWis = workItemsByTask.get(t.id) || [];
            const taskHasChildTasks = hasChildTasksById.get(t.id) === true;
            setDrawerWiId(null);
            openTaskRowDrawer(t, taskWis, taskHasChildTasks);
          }
        }}
        // UDRAW-S5-A7 — patches update `view`; the derived `drawerWi`
        // above picks up the change automatically. No parallel drawer
        // state to keep in sync.
        onWorkItemUpdated={(fresh) => {
          setView((v) => v && {
            ...v,
            workItems: v.workItems.map((w) => w.id === fresh.id ? { ...w, ...fresh } : w),
          });
        }}
        // UDRAW-S3 §2.2 — max nesting is 3 levels. Hide "+ child" on a
        // WI whose parent already has a parent (depth 3). Also hide on
        // Closed WIs (spec + prior toolbelt guard).
        canHaveChildren={!!drawerWi && drawerWi.status !== 'Closed' && (() => {
          // depth = 1 if root; 2 if wi has a parent; 3 if the parent
          // has a parent too. children of level 3 would be level 4 →
          // exceed cap.
          if (drawerWi.parentWorkItemId == null) return true;
          const p = (view?.workItems || []).find((w) => w.id === drawerWi.parentWorkItemId);
          if (!p) return true;
          return p.parentWorkItemId == null; // grand-parent exists → too deep
        })()}
        isBillable={view?.project?.isBillable !== false}
        // UDRAW-S3 §2.1 — pick up an auto-open request from the "+"
        // button / context menu. The drawer's own effect clears it
        // by not persisting the flag past mount.
        autoOpenCreate={autoCreateRequest?.kind === 'wi'
          && drawerWiId != null && autoCreateRequest.wiId === drawerWiId}
        onWorkItemCreated={(created, opts = {}) => {
          setAutoCreateRequest(null);
          if (created && created.id != null) {
            setView((v) => (v
              ? { ...v, workItems: [...(v.workItems || []), created] }
              : v));
            if (opts.navigate) setDrawerWiId(created.id);
            else toast('Created. Assign it, or someone can take it over.', { variant: 'success' });
          }
        }}
      />
      {/* QLOG-S2 (UDRAW-S2) — second drawer instance for info + quickLog
          modes. The unified TaskDrawerForm shell wraps both: PM/owner
          can edit Status + Notes and take ownership from either flow.
          The tree + navigation callback + in-place task update all
          travel through the mode-specific content object below. */}
      <WorkItemDrawer
        open={!!rowDrawer}
        onClose={() => setRowDrawer(null)}
        infoMode={rowDrawer?.kind === 'info'}
        // UDRAW-S5-A7 — item deleted by a peer while the drawer was
        // open. `rowDrawerInfoContent` is null when the anchoring
        // task/WI has vanished from view; the drawer's `missing`
        // branch renders a "no longer available" notice in that case.
        missing={!!rowDrawer && (
          (rowDrawer.taskId != null && !rowDrawerTask)
          || (rowDrawer.wiId != null && !rowDrawerWi)
        )}
        infoContent={rowDrawer?.kind === 'info' && rowDrawerInfoContent ? {
          ...rowDrawerInfoContent,
          // The info content itself now derives from `view` on every
          // render (see `rowDrawerInfoContent` useMemo), so
          // status / notes / comments / child rows follow live events
          // without any explicit copy-back.
          task: rowDrawerTask,
          projectId: view?.projectId ?? Number(id),
          team,
          caller,
          treeData: buildDrawerTreeData({ task: rowDrawerTask }, view, workItemsByTask),
          onTreeNavigate: (node) => {
            if (!node) return;
            if (node.kind === 'wi' && node.wi) {
              setRowDrawer(null);
              setDrawerWiId(node.wi.id);
              return;
            }
            if ((node.kind === 'task' || node.kind === 'phase') && node.task) {
              const nextT = node.task;
              const nextWis = workItemsByTask.get(nextT.id) || [];
              const nextHasChildren = hasChildTasksById.get(nextT.id) === true;
              openTaskRowDrawer(nextT, nextWis, nextHasChildren);
            }
          },
          onTaskChanged: (fresh) => {
            // UDRAW-S5-A7 — view update alone is enough; derived
            // `rowDrawerTask` picks up the change.
            setView((v) => v && {
              ...v,
              tasks: v.tasks.map((tt) => tt.id === fresh.id ? { ...tt, ...fresh } : tt),
            });
          },
          // ASGN-S1 §3.2 — the drawer's assignee context strip
          // exposes a "🕒 Log hours" affordance to the assignee.
          // The link calls this opener; DeliveryPage swaps the
          // current info drawer for the same task's QuickLog
          // drawer, which the drawer surface already knows how
          // to render (kind: 'quickLog').
          onOpenQuickLog: (taskId) => {
            setRowDrawer({ kind: 'quickLog', taskId });
          },
          // UDRAW-S3 §2.1 / §2.2 — bridge auto-open + created callback.
          isBillable: view?.project?.isBillable !== false,
          autoOpenCreate: autoCreateRequest?.kind === 'task'
            && rowDrawer?.taskId != null && autoCreateRequest.taskId === rowDrawer.taskId,
          onWorkItemCreated: (created, opts = {}) => {
            setAutoCreateRequest(null);
            if (created && created.id != null) {
              setView((v) => (v
                ? { ...v, workItems: [...(v.workItems || []), created] }
                : v));
              if (opts.navigate) {
                setRowDrawer(null);
                setDrawerWiId(created.id);
              } else {
                toast('Created. Assign it, or someone can take it over.', { variant: 'success' });
              }
            }
          },
          // UDRAW-S4 §2.1 — append the fresh comment into view; the
          // derived task reflects it on the next render.
          onCommentAdded: (taskId, freshComment) => {
            setView((v) => v && {
              ...v,
              tasks: v.tasks.map((tt) => tt.id === taskId
                ? { ...tt, comments: [...(tt.comments || []), freshComment] }
                : tt),
            });
          },
          // UDRAW-S5.1 §2.2.4 — peer-editing map scoped to this task.
          liveEditing: rowDrawerTask ? liveEditingFor(`t-${rowDrawerTask.id}`) : null,
        } : null}
        quickLogMode={rowDrawer?.kind === 'quickLog'}
        quickLogContext={rowDrawer?.kind === 'quickLog' && rowDrawerTask ? {
          projectId: view?.projectId ?? Number(id),
          projectName: view?.projectName,
          task: rowDrawerTask,
          team,
          caller,
          treeData: buildDrawerTreeData({ task: rowDrawerTask }, view, workItemsByTask),
          onTreeNavigate: (node) => {
            if (!node) return;
            if (node.kind === 'wi' && node.wi) {
              setRowDrawer(null);
              setDrawerWiId(node.wi.id);
              return;
            }
            if ((node.kind === 'task' || node.kind === 'phase') && node.task) {
              const nextT = node.task;
              const nextWis = workItemsByTask.get(nextT.id) || [];
              const nextHasChildren = hasChildTasksById.get(nextT.id) === true;
              openTaskRowDrawer(nextT, nextWis, nextHasChildren);
            }
          },
          onTaskChanged: (fresh) => {
            setView((v) => v && {
              ...v,
              tasks: v.tasks.map((tt) => tt.id === fresh.id ? { ...tt, ...fresh } : tt),
            });
          },
          // UDRAW-S3 §2.1 / §2.2 — same auto-open + created wiring as
          // the info path above.
          isBillable: view?.project?.isBillable !== false,
          autoOpenCreate: autoCreateRequest?.kind === 'task'
            && rowDrawer?.taskId != null && autoCreateRequest.taskId === rowDrawer.taskId,
          onWorkItemCreated: (created, opts = {}) => {
            setAutoCreateRequest(null);
            if (created && created.id != null) {
              setView((v) => (v
                ? { ...v, workItems: [...(v.workItems || []), created] }
                : v));
              if (opts.navigate) {
                setRowDrawer(null);
                setDrawerWiId(created.id);
              } else {
                toast('Created. Assign it, or someone can take it over.', { variant: 'success' });
              }
            }
          },
          // UDRAW-S4 §2.1 — same fresh-comment wiring as the info path.
          onCommentAdded: (taskId, freshComment) => {
            setView((v) => v && {
              ...v,
              tasks: v.tasks.map((tt) => tt.id === taskId
                ? { ...tt, comments: [...(tt.comments || []), freshComment] }
                : tt),
            });
          },
          // UDRAW-S5.1 §2.2.4 — peer-editing map scoped to this task.
          liveEditing: rowDrawerTask ? liveEditingFor(`t-${rowDrawerTask.id}`) : null,
        } : null}
        onChanged={reload}
      />
    </div>
  );
};

export default DeliveryPage;
